import AppConfig from '@/AppConfig';
import { QrCodeAmounts } from '@/classes/account';
import { Plan, Subscription } from '@/classes/stripe';
import store from '@/store';
import axios from 'axios';
import * as Sentry from '@sentry/vue';

export const enum PlanState {
  NO_PLAN = 'NO_PLAN',
  PLAN_INCOMPLETE = 'PLAN_INCOMPLETE',
  PLAN_ACTIVE = 'PLAN_ACTIVE',
  PLAN_UPCOMING = 'PLAN_UPCOMING',
  PLAN_INFERIOR = 'PLAN_INFERIOR',
  PLAN_DOWNGRADING = 'PLAN_DOWNGRADING',
  PLAN_CANCELLED = 'PLAN_CANCELLED',
  PLAN_SUPERIOR = 'PLAN_SUPERIOR',
}
/**
 * StripeSubscriptionService is responsible for handling the subscription business logic
 * and provides some swift helpers for components.
 */
export default class StripeSubscriptionService {
  // Active subscription from local storage or an empty one for new customers
  subscription: Subscription = new Subscription();

  // Load subscription from API/vuex and update vuex later on to keep a copy active for the customer session
  async initSubscription(): Promise<void | null> {
    if (!store.state.subscription.activeSubscription.id) {
      await store.dispatch('subscription/loadSubscription');
    }

    this.subscription = store.state.subscription.activeSubscription;

    return Promise.resolve();
  }

  // Store a subscription inside local storage
  async initStorage(subscription: Subscription | null = null): Promise<void | null> {
    return store.dispatch('subscription/setActiveSubscription', subscription ? subscription : this.subscription);
  }

  isPlanIncomplete(planId: string): boolean {
    return this.subscription.plan_id === planId && ['incomplete', 'past_due'].includes(this.subscription.status);
  }

  // Is the given planID registered as an active plan for the customers subscription
  isPlanActive(planId: string): boolean {
    return this.subscription.plan_id === planId && this.subscription.is_active;
  }

  // Is the given planID registered as an cancelled plan for the customers subscription
  isPlanCancelled(planId: string): boolean {
    return this.subscription.plan_id === planId && this.subscription.is_cancelled;
  }

  // Is the given planID planned as a downgrade from an existing plan? Old plan has been cancelled!
  isPlanUpcoming(planId: string): boolean {
    return this.subscription.new_plan_id === planId && this.subscription.is_cancelled;
  }

  // Check if current plan is in downgrade process
  isPlanDowngrading(planId: string): boolean {
    if (!this.subscription.new_plan_id) return false;
    return this.subscription.plan_id === planId && this.subscription.is_active && this.subscription.is_cancelled;
  }

  // Is the given plan inferior in comparision with the active plan. Moving to the monthly plan of the active
  // annual plan's tier (e.g. Advanced annual → Advanced monthly, Starter annual → Flex) counts as a downgrade.
  isPlanInferior(planId: string): boolean | null {
    const [askedPlan, activePlan] = [this.findPlan(planId), store.state.subscription.activePlan];
    return askedPlan && activePlan.planLevel >= 0
      ? askedPlan.planLevel < activePlan.planLevel || askedPlan.isMonthlyVariantOf(activePlan)
      : null;
  }

  // Is the given plan superior in comparison with the active plan
  isPlanSuperior(planId: string): boolean | null {
    const result = this.isPlanInferior(planId);
    return result !== null ? !result : null;
  }

  isEnterprisePlanChange(plan: Plan, intent: 'upgrade' | 'downgrade'): boolean | null {
    if (!plan.isEnterprisePlan) {
      return null;
    }

    const regularPlan = this.findPlan(plan.id);
    if (regularPlan) {
      return null;
    }

    const activeSubscription: Subscription = store.state.subscription.activeSubscription;
    const activePlan: Plan = activeSubscription?.plan;
    const hasActiveSubscription = !!activeSubscription && activeSubscription.id !== 0;
    const hasActivePlan = !!activePlan && activePlan.id !== 0;
    const targetLevel = Number(plan.planLevel);
    const activeLevel = Number(activePlan?.planLevel);
    const hasValidPlanLevels = Number.isFinite(targetLevel) && Number.isFinite(activeLevel);

    if (!hasActiveSubscription || !hasActivePlan || !hasValidPlanLevels) {
      return null;
    }

    const targetPrice = Number(plan.price.price);
    // activePlan is a `Plan` object and price is a `Currency` object,
    // but it's possible that the `price` property is not a `Currency` object because of `PlanTransformer.php`,
    // so we need to check if it's an object and get the price from the `Currency` object, if it's not an object
    // we use the `price` property directly
    const activePrice = Number(typeof activePlan.price === 'object' ? activePlan.price.price : activePlan.price);

    if (intent === 'upgrade') {
      if (targetLevel > activeLevel) {
        return true;
      }

      if (targetLevel === activeLevel && targetPrice > activePrice) {
        return true;
      }

      return false;
    }
    if (intent === 'downgrade') {
      return targetLevel < activeLevel;
    }

    return null;
  }

  isEnterprisePlanSuperior(plan: Plan): boolean | null {
    return this.isEnterprisePlanChange(plan, 'upgrade');
  }

  // Is the given plan on the active plan's level? The annual plan of the active monthly plan's tier (e.g. Advanced
  // monthly → Advanced annual) can share its level but is not equivalent: it counts as an upgrade.
  isPlanEquivalent(planId: string): boolean {
    const [askedPlan, activePlan] = [this.findPlan(planId), store.state.subscription.activePlan];
    return (
      askedPlan?.planLevel === activePlan?.planLevel && !(!!askedPlan && activePlan?.isMonthlyVariantOf(askedPlan))
    );
  }

  // Find a given plan via plan id inside local storage. Falls back to matching by product id when
  // the price id is no longer in the catalog (e.g. a legacy price replaced by a price increase),
  // since a plan's product is stable across price changes. Because a product can have several plans
  // (e.g. monthly vs yearly), the fallback narrows by the invoice currency and then deterministically
  // prefers the yearly variant, so a superseded annual price resolves to the correct label.
  findPlan(planId: string, productId?: string, currency?: string): Plan | null {
    const plans = store.state.subscription.plans;
    const byId = plans.find((v: Plan) => v.id == planId);
    if (byId) {
      return byId;
    }
    if (!productId) {
      return null;
    }
    let candidates = plans.filter((v: Plan) => v.product_id === productId);
    if (currency) {
      const byCurrency = candidates.filter((v: Plan) => v.currency === currency);
      if (byCurrency.length) {
        candidates = byCurrency;
      }
    }
    return candidates.find((v: Plan) => v.interval === 'year') ?? candidates[0] ?? null;
  }
  // Checks if the user can downgrade the plan. Returns false if the user has too many dynamic-codes
  canDowngradePlan(planId: string): boolean {
    // New plan
    const askedPlan = this.findPlan(planId);
    const amountUserDynamicCodes = store.state.account.account?.qrcodes?.activeDynamicCodes;
    // Code limit of new plan
    const newPlanCodeLimit = askedPlan ? parseInt(askedPlan.codeLimit) : 0;
    // Returns false if the user has too many dynamic-codes
    return newPlanCodeLimit >= amountUserDynamicCodes!;
  }

  // Returns if user can select the free plan.
  // Only possible if the user has 1 or less codes
  get canSelectFreePlan(): boolean | undefined {
    const { activeDynamicWebsiteCodes, activeStaticCodes, activeDynamicCodes } = store.state.account.account
      ?.qrcodes as QrCodeAmounts;

    if (
      (activeDynamicWebsiteCodes < 1 && activeDynamicCodes < 1) ||
      (activeDynamicWebsiteCodes === 1 && activeDynamicCodes < 2)
    ) {
      return activeStaticCodes <= 5;
    }

    return undefined;
  }

  // Cancel current subscription. reasonIdentifier is only collected on the
  // cancellation page; other entry points cancel without one.
  async cancel(resource_id: string, reasonIdentifier: string | null = null): Promise<any> {
    return axios
      .delete(AppConfig.getAPIBaseUrl() + '/subscription/stripe/' + resource_id, {
        // axios sends a DELETE body under `data`, not as the second argument.
        data: { reason_identifier: reasonIdentifier },
      })
      .then(async () => {
        await store.dispatch('subscription/loadSubscription');
        await store.commit('subscription/cancelSubscription', true);
      });
  }

  planState(planId: string): PlanState {
    if (this.subscription?.id === 0) return PlanState.NO_PLAN;

    if (this.isPlanIncomplete(planId)) return PlanState.PLAN_INCOMPLETE;
    if (this.isPlanActive(planId) && !this.isPlanCancelled(planId)) return PlanState.PLAN_ACTIVE;
    if (this.isPlanUpcoming(planId)) return PlanState.PLAN_UPCOMING;
    if (this.isPlanInferior(planId)) return PlanState.PLAN_INFERIOR;
    if (this.isPlanDowngrading(planId)) return PlanState.PLAN_DOWNGRADING;
    if (this.isPlanCancelled(planId)) return PlanState.PLAN_CANCELLED;
    if (this.isPlanEquivalent(planId)) return PlanState.PLAN_ACTIVE;
    if (this.isPlanSuperior(planId)) return PlanState.PLAN_SUPERIOR;

    return PlanState.NO_PLAN;
  }

  // Finds a plan by its own name (e.g. "advancedmonthly") or by its tier's name (e.g. "advanced"). A tier name
  // matches both its annual and monthly plan, so the interval decides between them (annual by default).
  getStandardPlanByName(planName: string, interval: string = 'year'): Plan | undefined {
    const requestedName = planName?.toLowerCase();
    const plansWithName: Plan[] = (store.state.subscription?.plans || []).filter(
      (plan: Plan) => plan.planName.toLowerCase() === requestedName || plan.tierName.toLowerCase() === requestedName
    );

    // "month" means the one-month plan, never a multi-month one.
    const matchesInterval = (plan: Plan) =>
      plan.interval === interval && (interval !== 'month' || (plan.interval_count ?? 1) === 1);

    return plansWithName.find(matchesInterval) || plansWithName[0];
  }

  async loadPlanById(planId: string): Promise<Plan | undefined> {
    if (planId?.startsWith('price_') === false) {
      Sentry.captureMessage(`Trying to load an invalid plan with id: ${planId}`);
      return;
    }

    const plan = store.state.subscription?.plans?.find((plan: Plan) => plan.id === planId);
    if (plan) return plan;
    try {
      const response = await axios.get<Plan>(AppConfig.getAPIBaseUrl() + '/subscription/stripe/plans/' + planId);
      return response.data;
    } catch (error) {
      Sentry.captureException(error);
    }
  }
}
